import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Clock, ChevronLeft, ChevronRight, Plus, Calendar, User, Pencil, Loader2 } from 'lucide-react';
import { Appointment } from '../types';
import { getAppointmentStartIso, isPendingDepositOutstanding } from '../utils/appointmentHelpers';
import { formatLocalYmd } from '../utils/timezoneUtils';

const DEFAULT_CATEGORY_BG = '#F9FAFB';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.trim();
  const match = /^#([0-9a-fA-F]{6})$/.exec(normalized);
  if (!match) return null;
  const intVal = parseInt(match[1], 16);
  return {
    r: (intVal >> 16) & 255,
    g: (intVal >> 8) & 255,
    b: intVal & 255
  };
}

function darkenHex(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const r = clamp(rgb.r * (1 - amount));
  const g = clamp(rgb.g * (1 - amount));
  const b = clamp(rgb.b * (1 - amount));
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

function getAppointmentCategoryBg(appointment: Appointment): string {
  const bg = appointment.assignments?.find(a => a.categoryBackgroundColor)?.categoryBackgroundColor;
  return (typeof bg === 'string' && bg.trim()) ? bg : DEFAULT_CATEGORY_BG;
}

type AppointmentBlock = {
  /** Stable key for rendering */
  id: string;
  /** Parent appointment (used for click/open details) */
  appointment: Appointment;
  /** Block start/end instants (UTC) */
  start: Date;
  end: Date;
  /** Assignments shown within this block (we render one block per assignment/service) */
  assignments: Appointment['assignments'];
  /** Professional for the block (if any) */
  professionalId?: string;
};

interface TimeOff {
  id: string;
  startDate: string;
  endDate: string;
  isAllDay: boolean | string | number;
  startTime?: string;
  endTime?: string;
  label?: string;
  professionalId?: string;
}

interface AppointmentCalendarProps {
  appointments: Appointment[];
  onAppointmentMove?: (appointmentId: string, newDate: Date, newTime: string) => void;
  onAppointmentClick?: (appointment: Appointment) => void;
  onAddAppointment?: (date: Date, time: string) => void;
  /** resourceId is the column id when in resource columns view (e.g. professional id for professional column view). */
  onTimeSlotDoubleClick?: (date: Date, time: string, resourceId?: string) => void;
  /** When set, the calendar shows a loading spinner on this appointment (e.g. while persisting drag/drop). */
  movingAppointmentId?: string | null;
  /** Resize handler for per-assignment blocks (15-min increments). */
  onAssignmentDurationChange?: (params: {
    appointmentId: string;
    assignmentId: string;
    durationMinutes: number;
  }) => void | Promise<void>;
  /**
   * Business-side override: allow creating appointments on time-off slots (e.g. owner/pro calendar),
   * while still showing time off indicators.
   * Default false to preserve existing behavior everywhere else.
   */
  allowBookingInTimeOffSlots?: boolean;
  /**
   * Mobile-only: enable horizontal swipe (1 day at a time) in the day view (dateRange mode).
   * Intended for business calendar UX; not used in customer booking.
   */
  onSwipeDayNavigate?: (direction: 'prev' | 'next') => void;
  /**
   * Optional: range navigation that should behave like the parent "date range" controls
   * (prev/next jumps by the current selected range: day/3 days/1 week, etc.).
   * When provided, we render large floating left/right buttons on the calendar.
   */
  onRangeNavigate?: (direction: 'prev' | 'next') => void;
  currentDate?: Date;
  viewMode?: 'month' | 'day';
  onViewModeChange?: (mode: 'month' | 'day') => void;
  dateRange?: { start: Date; end: Date };
  timeOffs?: TimeOff[];
  showProfessionalNames?: boolean;
  /** When false, professional names are hidden inside assignment blocks (service line shows only service name). Used with business setting "Show assigned professional next to service". Default true. */
  showProfessionalNameInAssignmentBlock?: boolean;
  operatingHourRange?: { startHour: number; endHour: number };
  /** If provided, only show blocks assigned to this professional (for "My Appointments" view) */
  currentProfessionalId?: string;
  /**
   * Optional: render a single-day "resource columns" layout (e.g. professional columns).
   * Only used when `dateRange` is provided and it represents a single day.
   * When omitted, the calendar behaves exactly as before.
   */
  resourceColumns?: Array<{ id: string; name: string }>;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  appointments: Appointment[];
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  appointments,
  onAppointmentMove,
  onAppointmentClick,
  onAddAppointment,
  onTimeSlotDoubleClick,
  movingAppointmentId,
  onAssignmentDurationChange,
  allowBookingInTimeOffSlots = false,
  onSwipeDayNavigate,
  onRangeNavigate,
  currentDate = new Date(),
  viewMode = 'month',
  onViewModeChange,
  dateRange,
  timeOffs = [],
  showProfessionalNames = false,
  showProfessionalNameInAssignmentBlock = true,
  operatingHourRange,
  currentProfessionalId,
  resourceColumns
}) => {
  const HOUR_ROW_HEIGHT_PX = 90; // 90px per hour row
  const DEFAULT_START_HOUR = 8;
  const DEFAULT_END_HOUR = 20;
  const HOUR_PADDING = 1;
  const RESOURCE_TIME_COL_WIDTH_PX = 80;
  const TIMELINE_TIME_COL_WIDTH_PX = 64; // w-16

  const [now, setNow] = useState<Date>(() => new Date());

  // Keep a live "current time" marker (updates every minute).
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const isSameLocalDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

  const getNowTopPx = (startHour: number) => {
    const minutesFromStart = (now.getHours() - startHour) * 60 + now.getMinutes();
    return minutesFromStart * (HOUR_ROW_HEIGHT_PX / 60);
  };

  const CurrentTimeLine: React.FC<{ topPx: number; leftPx: number }> = ({ topPx, leftPx }) => (
    <div
      className="absolute pointer-events-none"
      style={{ top: `${topPx}px`, left: `${leftPx}px`, right: 0, zIndex: 60 }}
      aria-hidden="true"
    >
      <div className="relative">
        <div className="w-full border-t-2 border-purple-600" />
        <div className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-purple-600" />
      </div>
    </div>
  );

  // Force day view when dateRange is provided (my-appointments page)
  const effectiveViewMode = dateRange ? 'day' : viewMode;
  const showFloatingRangeNav = Boolean(onRangeNavigate) && effectiveViewMode === 'day';
  const [portalReady, setPortalReady] = useState(false);
  const dayViewFrameRef = useRef<HTMLDivElement | null>(null);
  const [rangeNavAnchor, setRangeNavAnchor] = useState<{ leftPx: number; widthPx: number } | null>(null);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    if (!showFloatingRangeNav || !portalReady) return;

    let raf = 0;
    const compute = () => {
      const el = dayViewFrameRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const left = Math.max(0, Math.floor(rect.left));
      const width = Math.max(0, Math.floor(rect.width));
      setRangeNavAnchor({ leftPx: left, widthPx: width });
    };

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        compute();
      });
    };

    compute();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    const el = dayViewFrameRef.current;
    const ro = el ? new ResizeObserver(() => schedule()) : null;
    if (el && ro) ro.observe(el);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (ro) ro.disconnect();
    };
  }, [portalReady, showFloatingRangeNav]);

  const isResourceColumnsDayView = Boolean(
    dateRange &&
      Array.isArray(resourceColumns) &&
      resourceColumns.length > 0 &&
      new Date(dateRange.start).toDateString() === new Date(dateRange.end).toDateString()
  );

  const visibleHourRange = useMemo(() => {
    const baseStartHour = (() => {
      const start = operatingHourRange?.startHour;
      if (typeof start === 'number' && start >= 0 && start <= 23) return start;
      return DEFAULT_START_HOUR;
    })();

    const baseEndHour = (() => {
      const end = operatingHourRange?.endHour;
      if (typeof end === 'number' && end >= 0 && end <= 23) return end;
      return DEFAULT_END_HOUR;
    })();

    // Start from business operating hours (or default 8am-8pm), then expand if appointments fall outside.
    let startHour = baseStartHour;
    let endHour = Math.max(baseEndHour, baseStartHour);

    if (!appointments || appointments.length === 0) {
      return { startHour, endHour, slotCount: endHour - startHour + 1 };
    }

    const hours = appointments
      .map(a => {
        const iso = getAppointmentStartIso(a);
        if (!iso) return null;
        const d = new Date(iso);
        return Number.isFinite(d.getTime()) ? d.getHours() : null;
      })
      .filter((h): h is number => typeof h === 'number');

    if (hours.length === 0) {
      return { startHour, endHour, slotCount: endHour - startHour + 1 };
    }

    const minHour = Math.min(...hours);
    const maxHour = Math.max(...hours);
    startHour = Math.max(0, Math.min(startHour, minHour - HOUR_PADDING));
    endHour = Math.min(23, Math.max(endHour, maxHour + HOUR_PADDING));
    return { startHour, endHour, slotCount: endHour - startHour + 1 };
  }, [appointments, operatingHourRange]);

  const toTimeLabel = (hour: number) => {
    if (hour === 0) return '12:00 AM';
    if (hour < 12) return `${hour}:00 AM`;
    if (hour === 12) return '12:00 PM';
    return `${hour - 12}:00 PM`;
  };

  const toTime24 = (hour: number, minute: number = 0) => {
    const h = Math.max(0, Math.min(23, Math.floor(hour)));
    const m = Math.max(0, Math.min(59, Math.floor(minute)));
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  
  // Helper function to check if a time slot is within a time off period
  const isTimeSlotInTimeOff = useCallback((date: Date, hour: number, professionalId?: string): boolean => {
    if (!timeOffs || timeOffs.length === 0) return false;
    
    // Filter by professionalId if provided
    const relevantTimeOffs = professionalId 
      ? timeOffs.filter(to => to.professionalId === professionalId)
      : timeOffs;
    
    if (relevantTimeOffs.length === 0) return false;
    
    // Normalize the check date to start of day
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const checkDateTimestamp = checkDate.getTime();
    const checkDateStr = formatLocalYmd(checkDate); // YYYY-MM-DD format (local)
    
    // Create time slot boundaries
    const slotTime = new Date(checkDate);
    slotTime.setHours(hour, 0, 0, 0);
    const slotEndTime = new Date(checkDate);
    slotEndTime.setHours(hour + 1, 0, 0, 0);
    
    return relevantTimeOffs.some(timeOff => {
      try {
        // Parse dates - timeOff dates are strings in YYYY-MM-DD format
        // Extract just the date part (YYYY-MM-DD) if it's a full datetime string
        let startDateStr = String(timeOff.startDate);
        let endDateStr = String(timeOff.endDate);
        
        // If it's a full ISO string, extract just the date part
        if (startDateStr.includes('T')) {
          startDateStr = startDateStr.split('T')[0];
        }
        if (endDateStr.includes('T')) {
          endDateStr = endDateStr.split('T')[0];
        }
        
        // Simple string comparison for dates (YYYY-MM-DD format)
        // This is more reliable than Date object comparison
        if (checkDateStr < startDateStr || checkDateStr > endDateStr) {
          return false;
        }
        
        // If all day, the entire day is blocked
        // Handle both boolean and string/number values that might come from API
        const isAllDay = timeOff.isAllDay === true || 
          (typeof timeOff.isAllDay === 'string' && timeOff.isAllDay.toLowerCase() === 'true') ||
          (typeof timeOff.isAllDay === 'number' && timeOff.isAllDay === 1);
        
        if (isAllDay) {
          return true;
        }
        
        // For non-all-day time offs, we need startTime and endTime
        if (!timeOff.startTime || !timeOff.endTime) {
          return false;
        }
        
        // Check if the time slot overlaps with the time off period
        const [startHour, startMin] = timeOff.startTime.split(':').map(Number);
        const [endHour, endMin] = timeOff.endTime.split(':').map(Number);
        
        const timeOffStart = new Date(checkDate);
        timeOffStart.setHours(startHour, startMin || 0, 0, 0);
        const timeOffEnd = new Date(checkDate);
        timeOffEnd.setHours(endHour, endMin || 0, 0, 0);
        
        // Handle case where end time might be next day (e.g., 22:00 to 02:00)
        if (timeOffEnd.getTime() <= timeOffStart.getTime()) {
          timeOffEnd.setDate(timeOffEnd.getDate() + 1);
        }
        
        // Check if time slot overlaps with time off period
        // Overlaps if: slotStart < timeOffEnd AND slotEnd > timeOffStart
        return slotTime.getTime() < timeOffEnd.getTime() && slotEndTime.getTime() > timeOffStart.getTime();
      } catch (error) {
        console.error('Error checking time off:', error, timeOff);
        return false;
      }
    });
  }, [timeOffs]);
  
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [draggedAppointment, setDraggedAppointment] = useState<Appointment | null>(null);
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);
  const [dragOverSlotKey, setDragOverSlotKey] = useState<string | null>(null);
  const [dragOverSlotTime24, setDragOverSlotTime24] = useState<string | null>(null);
  const isDraggingRef = useRef(false);
  const dragPointerOffsetYRef = useRef<number>(0);
  const lastDropAtRef = useRef<number>(0);
  const isMoveInFlight = Boolean(movingAppointmentId);

  const [resizing, setResizing] = useState<{
    blockId: string;
    appointmentId: string;
    assignmentId: string;
    startClientY: number;
    startDurationMinutes: number;
    currentDurationMinutes: number;
  } | null>(null);
  const resizingRef = useRef<typeof resizing>(null);
  useEffect(() => {
    resizingRef.current = resizing;
  }, [resizing]);

  const snap15 = (mins: number) => Math.round(mins / 15) * 15;

  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [datePickerMonth, setDatePickerMonth] = useState(new Date());
  
  // Track which days are expanded to show all appointments
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  
  // Detect if device supports touch (mobile)
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // For "resource columns" day view, we want mobile to show exactly the first 2 non-time columns in-view.
  const resourceScrollRef = useRef<HTMLDivElement | null>(null);
  const [resourceMobileColWidthPx, setResourceMobileColWidthPx] = useState<number | null>(null);
  const [rangeDaysMobileColWidthPx, setRangeDaysMobileColWidthPx] = useState<number | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  // Desktop-only interactions: disable DnD/resizing on mobile/touch to avoid breaking scroll/gesture UX.
  const isDesktopInteractionEnabled = !isMobileViewport && !isTouchDevice;
  const canDragDrop = Boolean(onAppointmentMove) && isDesktopInteractionEnabled && !isMoveInFlight;
  const canResize = Boolean(onAssignmentDurationChange) && isDesktopInteractionEnabled && !isMoveInFlight;

  useEffect(() => {
    if (isDesktopInteractionEnabled) return;
    // If the user switches to mobile viewport (or touch), clear any in-progress drag/resize state.
    setDraggedAppointment(null);
    setDragOverDate(null);
    setDragOverSlotKey(null);
    setDragOverSlotTime24(null);
    setResizing(null);
  }, [isDesktopInteractionEnabled]);

  const dateRangeDayCount = useMemo(() => {
    if (!dateRange) return 0;
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return 0;
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000);
    return Math.max(1, diffDays + 1);
  }, [dateRange?.start ? new Date(dateRange.start).getTime() : 0, dateRange?.end ? new Date(dateRange.end).getTime() : 0]);

  const swipeContainerRef = useRef<HTMLDivElement | null>(null);
  const [swipeOffsetX, setSwipeOffsetX] = useState(0);
  const [swipeTransitionEnabled, setSwipeTransitionEnabled] = useState(false);
  const pendingSwipeDirectionRef = useRef<'prev' | 'next' | null>(null);

  const swipeTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipeLastRef = useRef<{ x: number; y: number } | null>(null);

  const canSwipeOneDay =
    Boolean(onSwipeDayNavigate) &&
    isMobileViewport &&
    isTouchDevice &&
    Boolean(dateRange) &&
    !isResourceColumnsDayView &&
    (dateRangeDayCount === 1 || dateRangeDayCount === 3);

  const getSwipeWidth = () => {
    const w = swipeContainerRef.current?.getBoundingClientRect().width;
    return typeof w === 'number' && w > 0 ? w : window.innerWidth;
  };

  // When the parent changes the day after a swipe, slide the new day in smoothly.
  useEffect(() => {
    if (!canSwipeOneDay) return;
    const dir = pendingSwipeDirectionRef.current;
    if (!dir) return;

    const width = getSwipeWidth();
    // Start off-screen opposite to the swipe direction, then animate to 0.
    setSwipeTransitionEnabled(false);
    setSwipeOffsetX(dir === 'next' ? width : -width);
    requestAnimationFrame(() => {
      setSwipeTransitionEnabled(true);
      setSwipeOffsetX(0);
      pendingSwipeDirectionRef.current = null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.start ? new Date(dateRange.start).getTime() : 0]);

  const handleSwipeTouchStart = (e: React.TouchEvent) => {
    if (!canSwipeOneDay) return;
    const t = e.touches[0];
    swipeTouchStartRef.current = { x: t.clientX, y: t.clientY };
    swipeLastRef.current = { x: t.clientX, y: t.clientY };
    setSwipeTransitionEnabled(false);
    setSwipeOffsetX(0);
  };

  const handleSwipeTouchMove = (e: React.TouchEvent) => {
    if (!canSwipeOneDay) return;
    const t = e.touches[0];
    swipeLastRef.current = { x: t.clientX, y: t.clientY };
    const start = swipeTouchStartRef.current;
    if (!start) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Only track mostly-horizontal drags; let vertical scrolling behave normally.
    if (Math.abs(dx) < Math.abs(dy)) return;
    setSwipeOffsetX(dx);
  };

  const handleSwipeTouchEnd = () => {
    if (!canSwipeOneDay) return;
    const start = swipeTouchStartRef.current;
    const end = swipeLastRef.current;
    swipeTouchStartRef.current = null;
    swipeLastRef.current = null;
    if (!start || !end) return;

    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Only trigger on deliberate horizontal swipe (avoid interfering with vertical scroll).
    if (absDx < 60 || absDx < absDy * 1.2) {
      // Snap back
      setSwipeTransitionEnabled(true);
      setSwipeOffsetX(0);
      return;
    }

    const direction: 'prev' | 'next' = dx < 0 ? 'next' : 'prev';
    const width = getSwipeWidth();
    setSwipeTransitionEnabled(true);
    setSwipeOffsetX(direction === 'next' ? -width : width);
    pendingSwipeDirectionRef.current = direction;
    window.setTimeout(() => {
      onSwipeDayNavigate?.(direction);
    }, 180);
  };
  
  useEffect(() => {
    // Check if device supports touch
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    return () => window.removeEventListener('resize', checkTouchDevice);
  }, []);

  useEffect(() => {
    const update = () => setIsMobileViewport(window.innerWidth < 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!isResourceColumnsDayView || !isMobileViewport) {
      setResourceMobileColWidthPx(null);
      return;
    }

    const el = resourceScrollRef.current;
    if (!el) return;

    const compute = () => {
      const containerWidth = el.clientWidth;
      if (!Number.isFinite(containerWidth) || containerWidth <= RESOURCE_TIME_COL_WIDTH_PX) {
        setResourceMobileColWidthPx(null);
        return;
      }

      const colsCount = Array.isArray(resourceColumns) ? resourceColumns.length : 0;
      const visibleNonTimeCols = Math.min(2, Math.max(1, colsCount));
      const width = Math.floor((containerWidth - RESOURCE_TIME_COL_WIDTH_PX) / visibleNonTimeCols);
      setResourceMobileColWidthPx(Math.max(80, width));
    };

    compute();

    // Keep sizing perfect as the container resizes (orientation changes, browser UI changes, etc.).
    const ro = new ResizeObserver(() => compute());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isResourceColumnsDayView, isMobileViewport, resourceColumns]);

  useEffect(() => {
    if (!dateRange || isResourceColumnsDayView || !isMobileViewport) {
      setRangeDaysMobileColWidthPx(null);
      return;
    }

    const el = resourceScrollRef.current;
    if (!el) return;

    const compute = () => {
      const containerWidth = el.clientWidth;
      if (!Number.isFinite(containerWidth) || containerWidth <= RESOURCE_TIME_COL_WIDTH_PX) {
        setRangeDaysMobileColWidthPx(null);
        return;
      }

      const nonTimeColsCount = Math.max(1, dateRangeDayCount);
      const visibleNonTimeCols = Math.min(3, nonTimeColsCount);
      const width = Math.floor((containerWidth - RESOURCE_TIME_COL_WIDTH_PX) / visibleNonTimeCols);
      setRangeDaysMobileColWidthPx(Math.max(80, width));
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(el);
    return () => ro.disconnect();
  }, [dateRange, dateRangeDayCount, isResourceColumnsDayView, isMobileViewport]);

  // Generate month options for dropdown (6 months back, current month, 6 months forward)
  const monthOptions = useMemo(() => {
    const options = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // Add 6 months back
    for (let i = 6; i >= 1; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      options.push({
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        value: date,
        isCurrent: false
      });
    }
    
    // Add current month
    const currentMonthDate = new Date(currentYear, currentMonth, 1);
    options.push({
      label: 'Current Month',
      value: currentMonthDate,
      isCurrent: true
    });
    
    // Add 6 months forward
    for (let i = 1; i <= 6; i++) {
      const date = new Date(currentYear, currentMonth + i, 1);
      options.push({
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        value: date,
        isCurrent: false
      });
    }
    
    return options;
  }, []);

  // Generate date picker calendar days
  const datePickerDays = useMemo(() => {
    const year = datePickerMonth.getFullYear();
    const month = datePickerMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      days.push({
        date,
        isCurrentMonth: date.getMonth() === month,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: date.toDateString() === selectedDate.toDateString()
      });
    }
    
    return days;
  }, [datePickerMonth, selectedDate]);



  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    // If date range is provided, only show days within that range
    if (dateRange) {
      const days: CalendarDay[] = [];
      const today = new Date();
      const start = new Date(dateRange.start);
      const end = new Date(dateRange.end);
      
      // Set time to start of day for comparison
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      
      // Generate days within the date range
      const currentDate = new Date(start);
      while (currentDate <= end) {
        const date = new Date(currentDate);
        
        const dayAppointments = appointments.filter(appointment => {
          const iso = getAppointmentStartIso(appointment);
          if (!iso) return false;
          const appointmentDate = new Date(iso);
          return appointmentDate.toDateString() === date.toDateString();
        });
        
        days.push({
          date,
          isCurrentMonth: true, // All days in range are considered current
          isToday: date.toDateString() === today.toDateString(),
          appointments: dayAppointments
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return days;
    }
    
    // Default behavior: show full month
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days: CalendarDay[] = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayAppointments = appointments.filter(appointment => {
        const iso = getAppointmentStartIso(appointment);
        if (!iso) return false;
        const appointmentDate = new Date(iso);
        return appointmentDate.toDateString() === date.toDateString();
      });
      
      days.push({
        date,
        isCurrentMonth: date.getMonth() === month,
        isToday: date.toDateString() === today.toDateString(),
        appointments: dayAppointments
      });
    }
    
    return days;
  }, [selectedDate, appointments, dateRange]);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return 'bg-green-500';
      case 'PENDING': return 'bg-yellow-500';
      case 'CANCELLED': return 'bg-red-500';
      case 'COMPLETED': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Get status text color
  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return 'text-green-700';
      case 'PENDING': return 'text-yellow-700';
      case 'CANCELLED': return 'text-red-700';
      case 'COMPLETED': return 'text-blue-700';
      default: return 'text-gray-700';
    }
  };

  const getBookingInitiatorUi = (
    appointment: Appointment
  ): { title: string; Icon: React.ElementType; badgeClassName: string; iconClassName: string } => {
    const initiator = appointment.bookingInitiator || 'BUSINESS';
    if (initiator === 'CUSTOMER') {
      return {
        title: 'Customer booked',
        Icon: User,
        badgeClassName: 'rounded-full bg-green-100 border-green-200',
        iconClassName: 'text-green-700'
      };
    }
    return {
      title: 'Booked by staff',
      Icon: Pencil,
      badgeClassName: 'rounded-md bg-orange-100 border-orange-200',
      iconClassName: 'text-orange-700'
    };
  };

  // Format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to get service names from assignments
  const getServiceNames = (appointment: Appointment) => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      return appointment.assignments.map((assignment) => assignment.serviceName).join(', ');
    }
    return 'No services assigned';
  };

  // Helper function to get service names as array
  const getServiceNamesArray = (appointment: Appointment): string[] => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      return appointment.assignments.map((assignment) => assignment.serviceName || 'Unknown Service');
    }
    return [];
  };

  // Helper function to build display lines per service: "Service (Professional)" when showProfessionalNameInAssignmentBlock is true
  const getServiceDisplayLines = (appointment: Appointment): Array<{ displayText: string; icon?: string | null }> => {
    if (!appointment.assignments || appointment.assignments.length === 0) return [];

    const byService = new Map<string, { pros: Set<string>; icon?: string | null }>();

    for (const assignment of appointment.assignments) {
      const serviceName = assignment.serviceName || 'Unknown Service';
      const resolvedIcon = typeof assignment.serviceIcon === 'string' && assignment.serviceIcon.trim() !== '' ? assignment.serviceIcon : null;
      const professionalName =
        showProfessionalNameInAssignmentBlock &&
        assignment.professionalFirstName &&
        assignment.professionalLastName
          ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
          : '';

      if (!byService.has(serviceName)) {
        byService.set(serviceName, { pros: new Set<string>(), icon: resolvedIcon });
      }
      if (professionalName) {
        byService.get(serviceName)!.pros.add(professionalName);
      }
      // Keep the first non-empty icon we find for this service
      if (!byService.get(serviceName)!.icon && resolvedIcon) {
        byService.get(serviceName)!.icon = resolvedIcon;
      }
    }

    return Array.from(byService.entries()).map(([serviceName, data]) => {
      const proList = Array.from(data.pros);
      const displayText = proList.length === 0 ? serviceName : `${serviceName} (${proList.join(', ')})`;
      return { displayText, icon: data.icon };
    });
  };

  const renderServiceIconBadge = (icon?: string | null) => {
    const raw = typeof icon === 'string' ? icon.trim() : '';
    const value = raw || '📦';
    const isImageSrc = /^https?:\/\//i.test(value) || value.startsWith('data:');

    return (
      <span className="flex-shrink-0 inline-flex items-center justify-center border w-4 h-4 md:w-3 md:h-3 rounded-md bg-gray-100 border-gray-200">
        {isImageSrc ? (
          <img src={value} alt="" className="w-3.5 h-3.5 md:w-2.5 md:h-2.5 object-contain" />
        ) : (
          <span className="text-xs md:text-[9px] leading-none text-gray-700">{value}</span>
        )}
      </span>
    );
  };

  // Helper function to calculate how many service lines can fit in available height.
  // Mobile uses a larger type size, so each line needs more vertical room.
  const calculateVisibleServices = (availableHeight: number, serviceCount: number): { visible: number; hasMore: boolean } => {
    const MIN_LINE_HEIGHT = isMobileViewport ? 20 : 12;
    const maxLines = Math.floor(availableHeight / MIN_LINE_HEIGHT);
    return {
      visible: Math.min(serviceCount, maxLines),
      hasMore: serviceCount > maxLines
    };
  };

  // Helper function to get professional names from assignments
  const getProfessionalNames = (appointment: Appointment) => {
    if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
      return '';
    }
    const professionals = appointment.assignments
      .map((assignment) => assignment.professionalFirstName && assignment.professionalLastName 
        ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.` 
        : null)
      .filter(Boolean);
    return Array.from(new Set(professionals)).join(', ');
  };

  // Helper function to get total duration from assignments
  const getTotalDuration = (appointment: Appointment) => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      return appointment.assignments.reduce((total, assignment) => total + assignment.durationMinutes, 0);
    }
    return 0;
  };

  const getBlockDurationMinutes = (block: AppointmentBlock) => {
    const ms = block.end.getTime() - block.start.getTime();
    if (!Number.isFinite(ms) || ms <= 0) return 0;
    return Math.round(ms / 60000);
  };

  const buildAppointmentBlocks = useCallback((appt: Appointment): AppointmentBlock[] => {
    const rawAssignments = Array.isArray(appt.assignments) ? appt.assignments : [];
    const apptStartIso = getAppointmentStartIso(appt);
    if (rawAssignments.length === 0) {
      if (!apptStartIso) return [];
      const start = new Date(apptStartIso);
      const end = new Date(start.getTime() + getTotalDuration(appt) * 60000);
      return [
        {
          id: `${appt.id}`,
          appointment: appt,
          start,
          end,
          assignments: rawAssignments,
          professionalId: undefined,
        }
      ];
    }

    const withTimes = rawAssignments
      .map((a) => {
        const start = a.fromDateTime ? new Date(a.fromDateTime) : null;
        let end = a.toDateTime ? new Date(a.toDateTime) : null;

        // 0-minute add-ons/charges can legitimately have end === start (zero-length).
        // Normalize to a tiny positive block so the calendar can render it without forcing
        // the entire appointment to fall back to sequential layout (which would distort other blocks).
        const durationMinutes = Number((a as any)?.durationMinutes ?? 0) || 0;
        if (
          start &&
          Number.isFinite(start.getTime()) &&
          durationMinutes === 0 &&
          (!end || !Number.isFinite(end.getTime()) || end.getTime() <= start.getTime())
        ) {
          end = new Date(start.getTime() + 60_000); // 1 minute
        }

        return { a, start, end };
      })
      .filter(({ start, end }) => start && end && Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) && end.getTime() > start.getTime()) as Array<{
        a: Appointment['assignments'][number];
        start: Date;
        end: Date;
      }>;

    // If we don't have per-assignment times, fall back to sequential blocks based on durations.
    // This preserves per-service rendering (including unassigned services) in resource column mode.
    if (withTimes.length !== rawAssignments.length) {
      if (!apptStartIso) return [];
      const apptStart = new Date(apptStartIso);
      if (!Number.isFinite(apptStart.getTime())) return [];

      let cursor = new Date(apptStart);
      return rawAssignments.map((a, idx) => {
        const minsRaw = (a as any)?.durationMinutes;
        const mins = (typeof minsRaw === 'number' && Number.isFinite(minsRaw) && minsRaw > 0) ? minsRaw : 1;
        const start = new Date(cursor);
        const end = new Date(start.getTime() + mins * 60000);
        cursor = new Date(end);
        return {
          id: `${appt.id}:${(a as any)?.id ?? idx}`,
          appointment: appt,
          start,
          end,
          assignments: [a],
          professionalId: (a as any)?.professionalId || undefined,
        };
      });
    }

    // Sort by time, then render one block per assignment (no compression/grouping).
    withTimes.sort((x, y) => x.start.getTime() - y.start.getTime());

    return withTimes.map((item) => ({
      id: `${appt.id}:${item.a.id}`,
      appointment: appt,
      start: item.start,
      end: item.end,
      assignments: [item.a],
      professionalId: item.a.professionalId || undefined
    }));
  }, [getTotalDuration]);

  // Helper function to get total price from assignments
  const getTotalPrice = (appointment: Appointment) => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      return appointment.assignments.reduce((total, assignment) => {
        const price = typeof assignment.price === 'number' ? assignment.price : 0;
        return total + price;
      }, 0);
    }
    return 0;
  };

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, appointment: Appointment) => {
    // Only enable drag UX when the parent opts in with an onAppointmentMove handler.
    if (!canDragDrop) return;
    setDraggedAppointment(appointment);
    isDraggingRef.current = true;
    // Capture the pointer offset from the top edge of the dragged block so we can snap based on
    // the block/widget top edge (not the cursor position).
    try {
      const el = e.currentTarget as HTMLElement | null;
      const rect = el?.getBoundingClientRect();
      const offset = rect ? (e.clientY - rect.top) : 0;
      dragPointerOffsetYRef.current = Number.isFinite(offset) ? Math.max(0, offset) : 0;
    } catch {
      dragPointerOffsetYRef.current = 0;
    }
    e.dataTransfer.effectAllowed = 'move';
    // Store an id so drops can be validated even if state is stale.
    try {
      e.dataTransfer.setData('text/plain', appointment.id);
      e.dataTransfer.setData('application/x-appointment-id', appointment.id);
    } catch {
      // no-op: some browsers may throw if dataTransfer is restricted
    }
  }, [canDragDrop]);

  // Handle drag over
  const handleDragOver = useCallback((e: React.DragEvent, date: Date) => {
    if (!canDragDrop) return;
    e.preventDefault();
    setDragOverDate(date);
  }, [canDragDrop]);

  const getSnappedQuarterTime24FromEvent = (e: React.DragEvent, hour: number) => {
    const el = e.currentTarget as HTMLElement | null;
    const rect = el?.getBoundingClientRect();
    const height = rect?.height || HOUR_ROW_HEIGHT_PX;
    const top = rect?.top ?? e.clientY;
    // Use the dragged block's top edge to determine snapping.
    const blockTopY = e.clientY - (dragPointerOffsetYRef.current || 0);
    const y = Math.max(0, Math.min(height - 1, blockTopY - top));
    const quarter = Math.max(0, Math.min(3, Math.floor((y / height) * 4)));
    return toTime24(hour, quarter * 15);
  };

  const getSlotKey = (date: Date, hour: number, columnId?: string) => {
    const dayKey = formatLocalYmd(date);
    const col = columnId ? String(columnId) : '';
    return `${dayKey}|${String(hour).padStart(2, '0')}|${col}`;
  };

  const isAppointmentMoving = useCallback(
    (appointment: Appointment | null | undefined) => {
      if (!movingAppointmentId || !appointment?.id) return false;
      return String(appointment.id) === String(movingAppointmentId);
    },
    [movingAppointmentId]
  );

  const getResizableAssignmentId = (block: AppointmentBlock): string | null => {
    const a0: any = Array.isArray(block.assignments) ? (block.assignments as any[])[0] : null;
    const id = a0?.id;
    return id ? String(id) : null;
  };

  const getEffectiveBlockDurationMinutes = (block: AppointmentBlock) => {
    if (resizing && resizing.blockId === block.id) return resizing.currentDurationMinutes;
    return getBlockDurationMinutes(block);
  };

  const startResize = (e: React.PointerEvent, block: AppointmentBlock) => {
    if (!canResize) return;
    const appointment = block.appointment;
    if (!appointment?.id) return;
    if (appointment.status === 'CANCELLED' || appointment.status === 'COMPLETED') return;
    const assignmentId = getResizableAssignmentId(block);
    if (!assignmentId) return;

    e.preventDefault();
    e.stopPropagation();

    const startDuration = Math.max(15, snap15(getBlockDurationMinutes(block)));
    const next = {
      blockId: block.id,
      appointmentId: String(appointment.id),
      assignmentId,
      startClientY: e.clientY,
      startDurationMinutes: startDuration,
      currentDurationMinutes: startDuration,
    };
    setResizing(next);

    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const onResizeMove = (e: React.PointerEvent) => {
    if (!resizingRef.current) return;
    e.preventDefault();
    const dy = e.clientY - resizingRef.current.startClientY;
    const deltaMinutes = snap15((dy * 60) / HOUR_ROW_HEIGHT_PX);
    const nextDuration = Math.max(15, resizingRef.current.startDurationMinutes + deltaMinutes);
    if (nextDuration === resizingRef.current.currentDurationMinutes) return;
    setResizing({ ...resizingRef.current, currentDurationMinutes: nextDuration });
  };

  const finishResize = async (e: React.PointerEvent) => {
    const cur = resizingRef.current;
    if (!cur) return;
    e.preventDefault();
    e.stopPropagation();

    const finalDuration = Math.max(15, snap15(cur.currentDurationMinutes));
    setResizing(null);

    // Persist only if it actually changed.
    if (!onAssignmentDurationChange) return;
    if (finalDuration === cur.startDurationMinutes) return;
    await onAssignmentDurationChange({
      appointmentId: cur.appointmentId,
      assignmentId: cur.assignmentId,
      durationMinutes: finalDuration,
    });
  };

  // Handle drop
  const handleDrop = useCallback((e: React.DragEvent, date: Date, time24?: string) => {
    if (!canDragDrop) return;
    if (!onAppointmentMove) return;
    e.preventDefault();
    e.stopPropagation();

    const droppedId = (() => {
      try {
        return e.dataTransfer.getData('application/x-appointment-id') || e.dataTransfer.getData('text/plain');
      } catch {
        return '';
      }
    })();

    if (draggedAppointment && draggedAppointment.id && (!droppedId || droppedId === draggedAppointment.id)) {
      const newDate = new Date(date);
      if (time24) {
        const [hRaw, mRaw] = time24.split(':');
        const h = Number.parseInt(hRaw, 10);
        const m = Number.parseInt(mRaw ?? '0', 10);
        newDate.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
      } else {
        const prevIso = getAppointmentStartIso(draggedAppointment);
        if (prevIso) {
          const prev = new Date(prevIso);
          newDate.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
        }
      }

      lastDropAtRef.current = Date.now();
      onAppointmentMove(draggedAppointment.id, newDate, formatTime(newDate.toISOString()));
    }
    setDraggedAppointment(null);
    setDragOverDate(null);
    setDragOverSlotKey(null);
    setDragOverSlotTime24(null);
  }, [draggedAppointment, onAppointmentMove, canDragDrop]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedAppointment(null);
    setDragOverDate(null);
    setDragOverSlotKey(null);
    setDragOverSlotTime24(null);
    setResizing(null);
    // Defer so a mouseup/click in the same tick doesn't open the details modal.
    window.setTimeout(() => {
      isDraggingRef.current = false;
    }, 0);
  }, []);

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Navigate to today


  // Get appointments for the selected day
  const getDayAppointments = (date?: Date) => {
    const targetDate = date || selectedDate;
    return appointments
      .filter(appointment => {
        const iso = getAppointmentStartIso(appointment);
        if (!iso) return false;
        const appointmentDate = new Date(iso);
        return appointmentDate.toDateString() === targetDate.toDateString();
      })
      .sort((a, b) => {
        const aIso = getAppointmentStartIso(a);
        const bIso = getAppointmentStartIso(b);
        return (aIso ? new Date(aIso).getTime() : 0) - (bIso ? new Date(bIso).getTime() : 0);
      });
  };

  const getDayBlocks = useCallback((date?: Date): AppointmentBlock[] => {
    const appts = getDayAppointments(date);
    let blocks = appts.flatMap((a) => buildAppointmentBlocks(a));
    
    // If currentProfessionalId is provided, filter to only show blocks assigned to this professional
    if (currentProfessionalId) {
      blocks = blocks.filter((block) => {
        // Include blocks where the professionalId matches, or blocks with no professionalId if they have assignments for this professional
        if (block.professionalId) {
          return String(block.professionalId) === String(currentProfessionalId);
        }
        // For blocks without explicit professionalId, check if any assignment in the block is for this professional
        return block.assignments.some((assignment) => 
          assignment.professionalId && String(assignment.professionalId) === String(currentProfessionalId)
        );
      });
    }
    
    // Sort by block start time for stable rendering
    blocks.sort((a, b) => a.start.getTime() - b.start.getTime());
    return blocks;
  }, [buildAppointmentBlocks, getDayAppointments, currentProfessionalId]);

  const getDayBlocksForResource = useCallback((date: Date, resourceId: string): AppointmentBlock[] => {
    const appts = getDayAppointments(date);
    let blocks = appts.flatMap((a) => buildAppointmentBlocks(a));

    const rid = String(resourceId);
    if (rid === '__unassigned__') {
      blocks = blocks.filter((block) => {
        // No professional on block, and no professional on any assignment => unassigned
        if (block.professionalId) return false;
        return !block.assignments.some((assignment) => Boolean(assignment.professionalId));
      });
      blocks.sort((a, b) => a.start.getTime() - b.start.getTime());
      return blocks;
    }

    blocks = blocks.filter((block) => {
      if (block.professionalId) {
        return String(block.professionalId) === rid;
      }
      return block.assignments.some((assignment) =>
        assignment.professionalId && String(assignment.professionalId) === rid
      );
    });

    blocks.sort((a, b) => a.start.getTime() - b.start.getTime());
    return blocks;
  }, [buildAppointmentBlocks, getDayAppointments]);

  // Get all days in the date range
  const getDaysInRange = () => {
    if (!dateRange) return [selectedDate];
    
    const days: Date[] = [];
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    
    const current = new Date(start);
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Navigate to previous day
  const goToPreviousDay = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  };

  // Navigate to next day
  const goToNextDay = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  };

  // Navigate to today
  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Handle click outside to close dropdowns
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Element;
    if (!target.closest('.month-dropdown')) {
      setIsMonthDropdownOpen(false);
    }
    if (!target.closest('.date-picker-dropdown')) {
      setIsDatePickerOpen(false);
    }
  }, []);

  // Add/remove click outside listener
  React.useEffect(() => {
    if (isMonthDropdownOpen || isDatePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMonthDropdownOpen, isDatePickerOpen, handleClickOutside]);

  // Sync date picker month with selected date
  React.useEffect(() => {
    setDatePickerMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
  }, [selectedDate]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header - Only show for regular appointments page (not my-appointments) */}
      {!dateRange && (
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Only show date label and calendar icon if dateRange is not provided (regular appointments page) */}
            {!dateRange && (
              <>
            <h2 className="text-2xl font-bold text-gray-900">
                  {effectiveViewMode === 'month' 
                ? selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                : selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })
              }
            </h2>
            
            {/* Conditional dropdown based on view mode */}
                {effectiveViewMode === 'month' ? (
              /* Month/Year Dropdown for month view */
              <div className="relative month-dropdown">
                <button
                  onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Calendar className="h-4 w-4" />
                </button>
                
                {isMonthDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 min-w-[280px]">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium text-gray-900">Select Month</h3>
                      <button
                        onClick={() => setIsMonthDropdownOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {monthOptions.map((option) => (
                        <button
                          key={option.value.toISOString()}
                          onClick={() => {
                            setSelectedDate(option.value);
                            setIsMonthDropdownOpen(false);
                          }}
                          className={`
                            w-full p-3 rounded-md text-sm transition-colors text-left
                            ${option.isCurrent
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'text-gray-900 hover:bg-blue-50'
                            }
                            ${selectedDate.getFullYear() === option.value.getFullYear() && 
                              selectedDate.getMonth() === option.value.getMonth()
                              ? 'ring-2 ring-blue-500'
                              : ''
                            }
                          `}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Date Picker for day view */
              <div className="relative date-picker-dropdown">
                <button
                  onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Calendar className="h-4 w-4" />
                </button>
                
                {isDatePickerOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 min-w-[280px]">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                      <button
                        onClick={() => setIsDatePickerOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Date picker navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => {
                          const newMonth = new Date(datePickerMonth);
                          newMonth.setMonth(newMonth.getMonth() - 1);
                          setDatePickerMonth(newMonth);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-medium">
                        {datePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        onClick={() => {
                          const newMonth = new Date(datePickerMonth);
                          newMonth.setMonth(newMonth.getMonth() + 1);
                          setDatePickerMonth(newMonth);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-xs text-gray-500 text-center py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1">
                      {datePickerDays.map((day, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedDate(day.date);
                            setIsDatePickerOpen(false);
                          }}
                          className={`
                            w-8 h-8 text-xs rounded-md transition-colors
                            ${!day.isCurrentMonth ? 'text-gray-300' : ''}
                            ${day.isToday ? 'bg-blue-100 text-blue-600 font-semibold' : ''}
                            ${day.isSelected ? 'bg-blue-600 text-white' : ''}
                            ${day.isCurrentMonth && !day.isToday && !day.isSelected ? 'hover:bg-gray-100' : ''}
                          `}
                        >
                          {day.date.getDate()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Navigation arrows with "This month" button between them */}
            <div className="flex items-center space-x-2">
              <button
                    onClick={effectiveViewMode === 'month' ? goToPreviousMonth : goToPreviousDay}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              </button>
              
              {/* "This month" button for month view, "Today" button for day view */}
                  {effectiveViewMode === 'month' ? (
                <button
                  onClick={goToToday}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  This month
                </button>
              ) : (
                <button
                  onClick={goToToday}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Today
                </button>
              )}
              
              <button
                    onClick={effectiveViewMode === 'month' ? goToNextMonth : goToNextDay}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="h-5 w-5 text-gray-600" />
              </button>
            </div>
              </>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            {/* View Mode Toggle - Only show if dateRange is not provided (regular appointments page) */}
            {!dateRange && onViewModeChange && (
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => onViewModeChange('month')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    effectiveViewMode === 'month'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => onViewModeChange('day')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    effectiveViewMode === 'day'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Day
                </button>
              </div>
            )}

            {/* Status Legend - Only show for regular appointments page (not my-appointments) */}
            {!dateRange && (
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Confirmed</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-gray-600">Pending</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">Cancelled</span>
              </div>
            </div>
            )}
            
            {onAddAppointment && (
              <button
                onClick={() => onAddAppointment(selectedDate, '09:00')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Appointment</span>
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Calendar Grid - Month View */}
      {effectiveViewMode === 'month' && (
        <div className="p-6">
          {/* Day Headers */}
          {dateRange ? (
            <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: `repeat(${calendarDays.length}, minmax(0, 1fr))` }}>
              {calendarDays.map((day, index) => (
                <div key={index} className="p-3 text-center">
                  <span className="text-sm font-medium text-gray-500">
                    {day.date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-3 text-center">
                <span className="text-sm font-medium text-gray-500">{day}</span>
              </div>
            ))}
          </div>
          )}

          {/* Calendar Days */}
          <div className={dateRange ? `grid gap-1` : `grid grid-cols-7 gap-1`} style={dateRange ? { gridTemplateColumns: `repeat(${calendarDays.length}, minmax(0, 1fr))` } : {}}>
            {calendarDays.map((day, index) => {
              const dayKey = day.date.toDateString();
              const isExpanded = expandedDays.has(dayKey);
              const maxVisible = isExpanded ? day.appointments.length : 3;
              const visibleAppointments = day.appointments.slice(0, maxVisible);
              
              const toggleExpand = (e: React.MouseEvent) => {
                e.stopPropagation();
                setExpandedDays(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(dayKey)) {
                    newSet.delete(dayKey);
                  } else {
                    newSet.add(dayKey);
                  }
                  return newSet;
                });
              };

              return (
                <div
                  key={index}
                  className={`
                    ${isExpanded ? 'min-h-[300px]' : 'min-h-[120px]'} p-2 border border-gray-200 rounded-lg transition-all duration-200
                    ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                    ${day.isToday ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}
                    ${dragOverDate && isSameLocalDay(dragOverDate, day.date) ? 'ring-2 ring-[#0B4778] ring-opacity-40' : ''}
                    ${!day.isCurrentMonth ? 'opacity-50' : ''}
                    hover:bg-gray-50 cursor-pointer
                    ${isExpanded ? 'overflow-y-auto max-h-[400px]' : ''}
                  `}
                  onDragOver={canDragDrop ? (e) => handleDragOver(e, day.date) : undefined}
                  onDrop={canDragDrop ? (e) => handleDrop(e, day.date) : undefined}
                  onClick={() => {
                    // Avoid creating a new appointment when the user just dropped one here.
                    if (Date.now() - lastDropAtRef.current < 350) return;
                    if (draggedAppointment) return;
                    onAddAppointment && onAddAppointment(day.date, '09:00');
                  }}
                >
                  {/* Day Number */}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`
                      text-sm font-medium
                      ${day.isToday ? 'text-blue-600' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    `}>
                      {day.date.getDate()}
                    </span>
                    {day.appointments.length > 0 && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">
                        {day.appointments.length}
                      </span>
                    )}
                  </div>

                  {/* Appointments */}
                  <div className="space-y-1">
                    {visibleAppointments.map((appointment) => (
                      (() => {
                        const categoryBg = getAppointmentCategoryBg(appointment);
                        const borderColor = darkenHex(categoryBg, 0.25);
                        const pendingDeposit = isPendingDepositOutstanding(appointment);
                        return (
                      <div
                        key={appointment.id}
                        draggable={
                          canDragDrop &&
                          appointment.status !== 'CANCELLED' &&
                          appointment.status !== 'COMPLETED'
                        }
                        onDragStart={
                          canDragDrop &&
                          appointment.status !== 'CANCELLED' &&
                          appointment.status !== 'COMPLETED'
                            ? (e) => handleDragStart(e, appointment)
                            : undefined
                        }
                        onDragEnd={canDragDrop ? handleDragEnd : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isDraggingRef.current) return;
                          if (isAppointmentMoving(appointment)) return;
                          onAppointmentClick && onAppointmentClick(appointment);
                        }}
                        title={pendingDeposit ? 'Pending deposit' : undefined}
                        className={`
                          relative p-2 rounded-md text-xs cursor-pointer transition-all duration-200
                          text-gray-700 hover:shadow-sm
                          ${pendingDeposit ? 'border-4 border-red-600' : 'border hover:shadow-sm'}
                          ${isAppointmentMoving(appointment) ? 'opacity-70 cursor-wait' : ''}
                        `}
                        style={{
                          backgroundColor: categoryBg,
                          ...(pendingDeposit ? {} : { borderColor }),
                        }}
                      >
                        {isAppointmentMoving(appointment) && (
                          <div className="absolute top-1 right-1 flex items-center">
                            <Loader2 className="h-3.5 w-3.5 text-[#0B4778] animate-spin" aria-label="Saving" />
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-2 h-2 rounded-full ${getStatusColor(appointment.status)}`}></div>
                            {(() => {
                              const { title, Icon, badgeClassName, iconClassName } = getBookingInitiatorUi(appointment);
                              return (
                                <span
                                  title={title}
                                  className={`inline-flex items-center justify-center border w-5 h-5 ${badgeClassName}`}
                                >
                                  <Icon className={`h-3.5 w-3.5 ${iconClassName}`} />
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="truncate text-gray-600">
                          {getServiceNames(appointment)}
                        </div>
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Clock className="h-3 w-3" />
                          <span>{formatTime(getAppointmentStartIso(appointment) ?? '') || '—'}</span>
                        </div>
                      </div>
                        );
                      })()
                    ))}
                    
                    {day.appointments.length > 3 && (
                      <button
                        onClick={toggleExpand}
                        className="w-full text-xs text-blue-600 hover:text-blue-800 hover:underline text-center py-1 transition-colors"
                      >
                        {isExpanded ? 'Show less' : `+${day.appointments.length - 3} more`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day View */}
      {effectiveViewMode === 'day' && (
        <div ref={dayViewFrameRef} className="relative px-2 pt-3 pb-4 md:px-6 md:pt-6 md:pb-6">
          {showFloatingRangeNav &&
            portalReady &&
            typeof document !== 'undefined' &&
            createPortal(
              <div
                className="pointer-events-none fixed top-1/2 z-20 flex items-center justify-between px-2"
                style={{
                  left: `${(rangeNavAnchor?.leftPx ?? 0)}px`,
                  width: `${(rangeNavAnchor?.widthPx ?? window.innerWidth)}px`,
                  // Slightly lower than true vertical center for better alignment with the visible calendar area.
                  transform: 'translateY(calc(-50% + 64px))'
                }}
              >
                <button
                  type="button"
                  className="pointer-events-auto rounded-full bg-white/20 hover:bg-white/35 border border-gray-200/50 shadow-sm shadow-black/5 backdrop-blur-sm transition-colors p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4778]/60"
                  aria-label="Previous date range"
                  title="Previous"
                  onClick={() => onRangeNavigate?.('prev')}
                >
                  <ChevronLeft className="w-8 h-8 text-gray-700/80" />
                </button>
                <button
                  type="button"
                  className="pointer-events-auto rounded-full bg-white/20 hover:bg-white/35 border border-gray-200/50 shadow-sm shadow-black/5 backdrop-blur-sm transition-colors p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4778]/60"
                  aria-label="Next date range"
                  title="Next"
                  onClick={() => onRangeNavigate?.('next')}
                >
                  <ChevronRight className="w-8 h-8 text-gray-700/80" />
                </button>
              </div>,
              document.body
            )}
          {dateRange ? (
            // Multi-day horizontal view for date range (my-appointments page)
            (() => {
              const MAX_VISIBLE_COLS_WITHOUT_SCROLL = 7;
              // For professional columns view with >7 columns, don't use min-w-full (enable horizontal scroll)
              // For all other cases (3 days, 1 week, or professional columns ≤7), use min-w-full to fill width
              const shouldNotFillWidth = isResourceColumnsDayView && resourceColumns && resourceColumns.length > MAX_VISIBLE_COLS_WITHOUT_SCROLL;
              
              return (
                <div ref={resourceScrollRef} className="overflow-x-auto">
                  <div className={shouldNotFillWidth ? "inline-block" : "inline-block min-w-full"}>
                    {/* Let the calendar grow naturally; rely on page scroll (avoid nested vertical scrollbars). */}
                    <div className="relative">
                      {isResourceColumnsDayView && (
                        <>
                          {(() => {
                            const day = new Date(dateRange!.start);
                            const cols = resourceColumns!;
                            const MAX_VISIBLE_COLS_WITHOUT_SCROLL = 7;
                            const PROFESSIONAL_COL_WIDTH_PX = 220;
                            
                            const useFlexibleWidth = !isMobileViewport && cols.length <= MAX_VISIBLE_COLS_WITHOUT_SCROLL;
                        
                        const resourceGridTemplateColumns = (() => {
                          if (isMobileViewport && resourceMobileColWidthPx) {
                            return `${RESOURCE_TIME_COL_WIDTH_PX}px repeat(${cols.length}, ${resourceMobileColWidthPx}px)`;
                          }
                          // Desktop: If 7 or fewer columns, use flexible width to fill container evenly
                          if (useFlexibleWidth) {
                            return `${RESOURCE_TIME_COL_WIDTH_PX}px repeat(${cols.length}, 1fr)`;
                          }
                          // Desktop: If more than 7 columns, use fixed width and enable horizontal scroll
                          return `${RESOURCE_TIME_COL_WIDTH_PX}px repeat(${cols.length}, ${PROFESSIONAL_COL_WIDTH_PX}px)`;
                        })();

                        const totalGridWidth = isMobileViewport && resourceMobileColWidthPx 
                          ? RESOURCE_TIME_COL_WIDTH_PX + (cols.length * resourceMobileColWidthPx)
                          : RESOURCE_TIME_COL_WIDTH_PX + (cols.length * PROFESSIONAL_COL_WIDTH_PX);
                        
                        return (
                          <>
                            {/* Header Row */}
                            <div className="bg-white border-b border-gray-200" style={useFlexibleWidth ? {} : { width: isMobileViewport ? 'auto' : `${totalGridWidth}px` }}>
                              <div className="grid" style={{ gridTemplateColumns: resourceGridTemplateColumns }}>
                                <div className="sticky left-0 z-30 p-3 font-semibold text-gray-700 border-r border-gray-200 bg-gray-50">
                                  Time
                                </div>
                                {cols.map((c) => (
                                  <div
                                    key={c.id}
                                    className="p-3 font-semibold text-gray-700 border-r border-gray-200 last:border-r-0 text-center"
                                    title={c.name}
                                  >
                                    <div className="text-base md:text-sm truncate">{c.name}</div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Time Slots and Appointments */}
                            <div
                              className="grid relative"
                              style={{
                                gridTemplateColumns: resourceGridTemplateColumns,
                                height: `${visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX}px`,
                                ...(useFlexibleWidth ? {} : { width: isMobileViewport ? 'auto' : `${totalGridWidth}px` })
                              }}
                            >
                              {(() => {
                                if (!isSameLocalDay(day, now)) return null;
                                const topPx = getNowTopPx(visibleHourRange.startHour);
                                if (topPx < 0 || topPx > visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX) return null;
                                return <CurrentTimeLine topPx={topPx} leftPx={RESOURCE_TIME_COL_WIDTH_PX} />;
                              })()}

                              {/* Time Labels Column (sticky on horizontal scroll) */}
                              <div className="sticky left-0 z-20 h-full border-r border-gray-200 bg-gray-50">
                                {Array.from({ length: visibleHourRange.slotCount }, (_, i) => {
                                  const hour = i + visibleHourRange.startHour;
                                  return (
                                    <div
                                      key={i}
                                      className="flex items-start justify-end pr-2 border-b border-gray-100 box-border"
                                      style={{ height: `${HOUR_ROW_HEIGHT_PX}px` }}
                                    >
                                      <span className="text-sm md:text-xs text-gray-500 font-medium">{toTimeLabel(hour)}</span>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Professional Columns */}
                              {cols.map((c) => {
                                // Keep a stable "resource id" for time off lookup. For the Unassigned column,
                                // we intentionally pass "__unassigned__" so it matches no real professionalId.
                                const professionalId = c.id;
                                const hasTimeOffBlocksForCol =
                                  professionalId !== '__unassigned__' && timeOffs.some((to) => to.professionalId === professionalId);
                                
                                return (
                                  <div key={c.id} className="relative h-full border-r border-gray-200 last:border-r-0 overflow-hidden">
                                  {/* Clickable Time Slots */}
                                  <div className="absolute inset-0">
                                    {Array.from({ length: visibleHourRange.slotCount }, (_, i) => {
                                      const hour = i + visibleHourRange.startHour;
                                      const time24 = toTime24(hour);
                                      const isInTimeOff = isTimeSlotInTimeOff(day, hour, professionalId);
                                      const canCreateInThisSlot = allowBookingInTimeOffSlots || !isInTimeOff;

                                      const handleTimeSlotClick = () => {
                                        if (onTimeSlotDoubleClick && canCreateInThisSlot) {
                                          onTimeSlotDoubleClick(day, time24, professionalId);
                                        }
                                      };

                                      return (
                                        <div
                                          key={i}
                                          className={`border-b border-gray-100 transition-colors relative group box-border ${
                                            isInTimeOff && !allowBookingInTimeOffSlots
                                              ? 'bg-gray-100 cursor-not-allowed opacity-60'
                                              : isTouchDevice
                                                ? 'cursor-pointer active:bg-blue-50'
                                                : 'hover:bg-blue-50 cursor-pointer'
                                          }`}
                                          style={{ height: `${HOUR_ROW_HEIGHT_PX}px` }}
                                          onClick={isTouchDevice && canCreateInThisSlot ? handleTimeSlotClick : undefined}
                                          onDoubleClick={!isTouchDevice && canCreateInThisSlot ? handleTimeSlotClick : undefined}
                                          onDragOver={
                                            canDragDrop && canCreateInThisSlot
                                              ? (e) => {
                                                  handleDragOver(e, day);
                                                  const snapped = getSnappedQuarterTime24FromEvent(e, hour);
                                                  setDragOverSlotKey(getSlotKey(day, hour, professionalId));
                                                  setDragOverSlotTime24(snapped);
                                                }
                                              : undefined
                                          }
                                          onDrop={
                                            canDragDrop && canCreateInThisSlot
                                              ? (e) => {
                                                  const snapped = getSnappedQuarterTime24FromEvent(e, hour);
                                                  handleDrop(e, day, snapped);
                                                }
                                              : undefined
                                          }
                                          title={
                                            isInTimeOff
                                              ? allowBookingInTimeOffSlots
                                                ? 'Time off (booking allowed)'
                                                : 'Time off - unavailable'
                                              : isTouchDevice
                                                ? 'Tap to create appointment'
                                                : 'Double-click to create appointment'
                                          }
                                        >
                                          {canDragDrop &&
                                            dragOverSlotKey === getSlotKey(day, hour, professionalId) &&
                                            dragOverSlotTime24 && (
                                              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/20">
                                                  {dragOverSlotTime24}
                                                </span>
                                              </div>
                                            )}
                                          {/* Time off indicator (avoid double-rendering under labeled time-off blocks) */}
                                          {isInTimeOff && !hasTimeOffBlocksForCol && (
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                              <div className="flex items-center gap-1 text-gray-500 text-sm md:text-xs">
                                                <i className="bi bi-calendar-x"></i>
                                                <span>Time Off</span>
                                              </div>
                                            </div>
                                          )}
                                          {/* Add icon indicator */}
                                          {(!isInTimeOff || allowBookingInTimeOffSlots) && (
                                            <div
                                              className={`absolute inset-0 flex items-center justify-center ${
                                                isTouchDevice ? 'opacity-30' : 'opacity-0 group-hover:opacity-30'
                                              } transition-opacity pointer-events-none`}
                                            >
                                              <Plus className="w-5 h-5 text-blue-600" />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Appointments for this professional */}
                                  {(() => {
                                    const blocksForCol = getDayBlocksForResource(day, c.id);

                                    const blockGroups: AppointmentBlock[][] = [];
                                    const sortedBlocks = [...blocksForCol].sort((a, b) => a.start.getTime() - b.start.getTime());

                                    sortedBlocks.forEach((block) => {
                                      const blockStart = block.start.getTime();
                                      const blockEnd = block.end.getTime();
                                      let addedToGroup = false;

                                      for (const group of blockGroups) {
                                        const groupEnd = Math.max(...group.map((b) => b.end.getTime()));
                                        const groupStart = Math.min(...group.map((b) => b.start.getTime()));

                                        if (blockStart < groupEnd && blockEnd > groupStart) {
                                          group.push(block);
                                          addedToGroup = true;
                                          break;
                                        }
                                      }

                                      if (!addedToGroup) blockGroups.push([block]);
                                    });

                                    return blockGroups.map((group) =>
                                      group.map((block, idx) => {
                                        const appointment = block.appointment;
                                        const appointmentTime = block.start;
                                        const hour = appointmentTime.getHours();
                                        const minute = appointmentTime.getMinutes();
                                        const topPosition =
                                          ((hour - visibleHourRange.startHour) * 60 + minute) * (HOUR_ROW_HEIGHT_PX / 60);

                                        const calculatedHeight = getEffectiveBlockDurationMinutes(block) * (HOUR_ROW_HEIGHT_PX / 60);
                                        const appointmentHeight = Math.max(40, calculatedHeight);

                                        const spacingPercent = 1;
                                        const containerWidth = 100;
                                        const totalSpacing = spacingPercent * (group.length - 1);
                                        const availableWidth = containerWidth - totalSpacing;
                                        const appointmentWidth = availableWidth / group.length;
                                        const leftPosition = idx * (appointmentWidth + spacingPercent);

                                        const servicesForBlock = block.assignments || [];
                                        const isCanceled = appointment.status === 'CANCELLED';
                                        const blockCategoryBg = isCanceled 
                                          ? '#E5E7EB' // Light gray for canceled appointments
                                          : (servicesForBlock.find(a => a.categoryBackgroundColor)?.categoryBackgroundColor || DEFAULT_CATEGORY_BG) as string;
                                        const blockBorderColor = darkenHex(blockCategoryBg, 0.25);
                                        const pendingDeposit = isPendingDepositOutstanding(appointment);

                                        // Zig-zag pattern for canceled appointments
                                        const zigZagPattern = isCanceled
                                          ? `repeating-linear-gradient(
                                              45deg,
                                              transparent,
                                              transparent 6px,
                                              rgba(0, 0, 0, 0.04) 6px,
                                              rgba(0, 0, 0, 0.04) 12px
                                            )`
                                          : 'none';

                                        return (
                                          <div
                                            key={block.id}
                                            draggable={
                                              canDragDrop &&
                                              appointment.status !== 'CANCELLED' &&
                                              appointment.status !== 'COMPLETED'
                                            }
                                            onDragStart={
                                              canDragDrop &&
                                              appointment.status !== 'CANCELLED' &&
                                              appointment.status !== 'COMPLETED'
                                                ? (e) => handleDragStart(e, appointment)
                                                : undefined
                                            }
                                            onDragEnd={canDragDrop ? handleDragEnd : undefined}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (isDraggingRef.current) return;
                                              if (isAppointmentMoving(appointment)) return;
                                              onAppointmentClick && onAppointmentClick(appointment);
                                            }}
                                            title={pendingDeposit ? 'Pending deposit' : undefined}
                                            className={`
                                              absolute px-2 py-1 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden
                                              text-gray-700
                                              ${pendingDeposit ? 'border-4 border-red-600 border-solid' : 'border-l-4 border-t'}
                                              ${isAppointmentMoving(appointment) ? 'opacity-70 cursor-wait' : ''}
                                            `}
                                            style={{
                                              top: `${topPosition}px`,
                                              left: `${leftPosition}%`,
                                              width: `${appointmentWidth}%`,
                                              height: `${appointmentHeight}px`,
                                              maxHeight: `${appointmentHeight}px`,
                                              zIndex: 10,
                                              boxSizing: 'border-box',
                                              backgroundColor: blockCategoryBg,
                                              backgroundImage: zigZagPattern,
                                              ...(pendingDeposit
                                                ? {}
                                                : {
                                                    borderLeftColor: blockBorderColor,
                                                    borderTopColor: blockBorderColor,
                                                  }),
                                            }}
                                          >
                                            {isAppointmentMoving(appointment) && (
                                              <div className="absolute top-1 right-1 z-10">
                                                <Loader2 className="h-3.5 w-3.5 text-[#0B4778] animate-spin" aria-label="Saving" />
                                              </div>
                                            )}
                                            {canResize &&
                                              appointment.status !== 'CANCELLED' &&
                                              appointment.status !== 'COMPLETED' &&
                                              getResizableAssignmentId(block) && (
                                                <div
                                                  className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize"
                                                  onPointerDown={(e) => startResize(e, block)}
                                                  onPointerMove={resizing?.blockId === block.id ? onResizeMove : undefined}
                                                  onPointerUp={resizing?.blockId === block.id ? finishResize : undefined}
                                                >
                                                  <div className="mx-auto mt-0.5 h-1 w-10 rounded-full bg-gray-400/60" />
                                                </div>
                                              )}
                                            <div className="flex flex-col h-full overflow-hidden justify-start pt-0.5">
                                              <div className="flex items-start gap-1.5 mb-0.5 min-w-0">
                                                {(() => {
                                                  const { title, Icon, badgeClassName, iconClassName } = getBookingInitiatorUi(appointment);
                                                  return (
                                                    <span
                                                      title={title}
                                                      className={`flex-shrink-0 inline-flex items-center justify-center border w-4 h-4 md:w-3 md:h-3 ${badgeClassName}`}
                                                    >
                                                      <Icon className={`h-3 w-3 md:h-2.5 md:w-2.5 ${iconClassName}`} />
                                                    </span>
                                                  );
                                                })()}
                                                <div className="font-semibold text-base md:text-xs leading-snug md:leading-tight min-w-0 truncate text-gray-900">
                                                  {appointment.customer_first_name && appointment.customer_last_name
                                                    ? `${appointment.customer_first_name} ${appointment.customer_last_name}`
                                                    : appointment.customer_first_name || appointment.customer_last_name || 'Customer'}
                                                  {isCanceled && <span className="text-red-500 font-semibold ml-1">(Canceled)</span>}
                                                </div>
                                              </div>

                                              {(() => {
                                                const services = getServiceDisplayLines({ ...appointment, assignments: servicesForBlock });
                                                if (services.length === 0) return null;

                                                const availableHeight = appointmentHeight - (isMobileViewport ? 32 : 22);
                                                const { visible, hasMore } = calculateVisibleServices(availableHeight, services.length);
                                                const visibleServices = services.slice(0, visible);

                                                return (
                                                  <div className="flex flex-col gap-0.5 mt-0.5 overflow-hidden">
                                                    {visibleServices.map((service, idx2) => (
                                                      <div
                                                        key={idx2}
                                                        className="text-sm md:text-xs text-gray-600 truncate leading-snug md:leading-tight flex items-center gap-1"
                                                        title={service.displayText}
                                                      >
                                                        {renderServiceIconBadge(service.icon)}
                                                        <span>{service.displayText}</span>
                                                      </div>
                                                    ))}
                                                    {hasMore && <div className="text-sm md:text-xs text-gray-500 leading-snug md:leading-tight">...</div>}
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        );
                                      })
                                    );
                                  })()}

                                  {/* Time Off Blocks for this professional */}
                                  {professionalId !== '__unassigned__' && (() => {
                                    const professionalTimeOffs = timeOffs.filter(to => to.professionalId === professionalId);
                                    if (professionalTimeOffs.length === 0) return null;

                                    const dayStr = formatLocalYmd(day);
                                    
                                    return professionalTimeOffs
                                      .filter(timeOff => {
                                        let startDateStr = String(timeOff.startDate);
                                        let endDateStr = String(timeOff.endDate);
                                        
                                        if (startDateStr.includes('T')) {
                                          startDateStr = startDateStr.split('T')[0];
                                        }
                                        if (endDateStr.includes('T')) {
                                          endDateStr = endDateStr.split('T')[0];
                                        }
                                        
                                        return dayStr >= startDateStr && dayStr <= endDateStr;
                                      })
                                      .map(timeOff => {
                                        const isAllDay = timeOff.isAllDay === true || 
                                          (typeof timeOff.isAllDay === 'string' && timeOff.isAllDay.toLowerCase() === 'true') ||
                                          (typeof timeOff.isAllDay === 'number' && timeOff.isAllDay === 1);
                                        
                                        let startHour = visibleHourRange.startHour;
                                        let startMinute = 0;
                                        let endHour = visibleHourRange.endHour;
                                        let endMinute = 0;
                                        
                                        if (!isAllDay && timeOff.startTime && timeOff.endTime) {
                                          const [startH, startM] = timeOff.startTime.split(':').map(Number);
                                          const [endH, endM] = timeOff.endTime.split(':').map(Number);
                                          startHour = startH;
                                          startMinute = startM || 0;
                                          endHour = endH;
                                          endMinute = endM || 0;
                                          
                                          // Adjust if end time is next day
                                          if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
                                            endHour += 24;
                                          }
                                        }
                                        
                                        // Calculate position and height
                                        const topPosition = ((startHour - visibleHourRange.startHour) * 60 + startMinute) * (HOUR_ROW_HEIGHT_PX / 60);
                                        const durationMinutes = ((endHour - startHour) * 60) + (endMinute - startMinute);
                                        const blockHeight = Math.max(30, durationMinutes * (HOUR_ROW_HEIGHT_PX / 60));
                                        
                                        // Clamp to visible range
                                        const clampedTop = Math.max(0, topPosition);
                                        const clampedHeight = Math.min(blockHeight, (visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX) - clampedTop);
                                        
                                        return (
                                          <div
                                            key={timeOff.id}
                                            className="absolute left-0 right-0 bg-gray-200/80 border-l-4 border-gray-400 pointer-events-none z-0"
                                            style={{
                                              top: `${clampedTop}px`,
                                              height: `${clampedHeight}px`,
                                              minHeight: '30px'
                                            }}
                                            title={timeOff.label || 'Time Off'}
                                          >
                                            <div className="flex items-center justify-center h-full px-2">
                                              <div className="flex items-center gap-1 text-gray-700 text-sm md:text-xs font-medium">
                                                <i className="bi bi-calendar-x"></i>
                                                <span className="truncate">{timeOff.label || 'Time Off'}</span>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      });
                                  })()}
                                </div>
                              );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}

                  {!isResourceColumnsDayView && (
                    <>
                      {(() => {
                        const days = getDaysInRange();
                        // We intentionally keep the time column OUTSIDE the swipe-transformed element,
                        // so on mobile swipes only move the day columns (time labels stay stationary).
                        const rangeDayColsGridTemplateColumns = (() => {
                          if (isMobileViewport && rangeDaysMobileColWidthPx) {
                            return `repeat(${days.length}, ${rangeDaysMobileColWidthPx}px)`;
                          }
                          // Use flexible width for multi-day range views (3 days, 1 week) to fill available space evenly
                          return `repeat(${days.length}, minmax(100px, 1fr))`;
                        })();

                        return (
                          <>
                            {/* Header Row */}
                            <div className="bg-white border-b border-gray-200">
                              <div className="flex">
                                <div
                                  className="sticky left-0 z-30 p-3 font-semibold text-gray-700 border-r border-gray-200 bg-gray-50 shrink-0"
                                  style={{ width: `${RESOURCE_TIME_COL_WIDTH_PX}px` }}
                                >
                                  Time
                                </div>
                                <div className="flex-1 overflow-hidden">
                                  <div
                                    className="grid"
                                    style={{
                                      gridTemplateColumns: rangeDayColsGridTemplateColumns,
                                      transform: canSwipeOneDay ? `translate3d(${swipeOffsetX}px, 0, 0)` : undefined,
                                      transition:
                                        canSwipeOneDay && swipeTransitionEnabled ? 'transform 180ms ease-out' : undefined
                                    }}
                                  >
                                    {days.map((day, dayIndex) => (
                                      <div
                                        key={dayIndex}
                                        className="p-3 font-semibold text-gray-700 border-r border-gray-200 last:border-r-0 text-center"
                                      >
                                        <div className="text-base md:text-sm">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                        <div className="text-sm md:text-xs text-gray-500">{day.getDate()}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Time Slots and Appointments */}
                            <div className="flex">
                              {/* Time Labels Column (sticky on horizontal scroll; NOT swipe-transformed) */}
                              <div
                                className="sticky left-0 z-20 h-full border-r border-gray-200 bg-gray-50 shrink-0"
                                style={{ width: `${RESOURCE_TIME_COL_WIDTH_PX}px`, height: `${visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX}px` }}
                              >
                                {Array.from({ length: visibleHourRange.slotCount }, (_, i) => {
                                  const hour = i + visibleHourRange.startHour;
                                  return (
                                    <div
                                      key={i}
                                      className="flex items-start justify-end pr-2 border-b border-gray-100 box-border"
                                      style={{ height: `${HOUR_ROW_HEIGHT_PX}px` }}
                                    >
                                      <span className="text-sm md:text-xs text-gray-500 font-medium">{toTimeLabel(hour)}</span>
                                    </div>
                                  );
                                })}
                              </div>

                              <div className="flex-1 overflow-hidden">
                                <div
                                  ref={swipeContainerRef}
                                  className="grid relative"
                                  style={{
                                    gridTemplateColumns: rangeDayColsGridTemplateColumns,
                                    height: `${visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX}px`,
                                    transform: canSwipeOneDay ? `translate3d(${swipeOffsetX}px, 0, 0)` : undefined,
                                    transition:
                                      canSwipeOneDay && swipeTransitionEnabled ? 'transform 180ms ease-out' : undefined,
                                    touchAction: canSwipeOneDay ? 'pan-y' : undefined
                                  }}
                                  onTouchStart={handleSwipeTouchStart}
                                  onTouchMove={handleSwipeTouchMove}
                                  onTouchEnd={handleSwipeTouchEnd}
                                >
                                  {(() => {
                                    const hasToday = days.some((d) => isSameLocalDay(d, now));
                                    if (!hasToday) return null;
                                    const topPx = getNowTopPx(visibleHourRange.startHour);
                                    if (topPx < 0 || topPx > visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX) return null;
                                    // Inside the swipe container, the line should start at the left edge (time column is outside).
                                    return <CurrentTimeLine topPx={topPx} leftPx={0} />;
                                  })()}

                                  {/* Day Columns */}
                                  {days.map((day, dayIndex) => (
                                    <div
                                      key={dayIndex}
                                      className="relative h-full border-r border-gray-200 last:border-r-0 overflow-hidden"
                                    >
                                  {/* Clickable Time Slots */}
                                  <div className="absolute inset-0">
                                    {Array.from({ length: visibleHourRange.slotCount }, (_, i) => {
                                      const hour = i + visibleHourRange.startHour;
                                      const time24 = toTime24(hour, 0);
                                      const isInTimeOff = isTimeSlotInTimeOff(day, hour);
                                      const canCreateInThisSlot = allowBookingInTimeOffSlots || !isInTimeOff;

                                      const handleTimeSlotClick = () => {
                                        if (onTimeSlotDoubleClick && canCreateInThisSlot) {
                                          onTimeSlotDoubleClick(day, time24);
                                        }
                                      };

                                      return (
                                        <div
                                          key={i}
                                          className={`border-b border-gray-100 transition-colors relative group box-border ${
                                            isInTimeOff && !allowBookingInTimeOffSlots
                                              ? 'bg-gray-100 cursor-not-allowed opacity-60'
                                              : isTouchDevice
                                                ? 'cursor-pointer active:bg-blue-50'
                                                : 'hover:bg-blue-50 cursor-pointer'
                                          }`}
                                          style={{ height: `${HOUR_ROW_HEIGHT_PX}px` }}
                                          onClick={isTouchDevice && canCreateInThisSlot ? handleTimeSlotClick : undefined}
                                          onDoubleClick={!isTouchDevice && canCreateInThisSlot ? handleTimeSlotClick : undefined}
                                          onDragOver={
                                            canDragDrop && canCreateInThisSlot
                                              ? (e) => {
                                                  handleDragOver(e, day);
                                                  const snapped = getSnappedQuarterTime24FromEvent(e, hour);
                                                  setDragOverSlotKey(getSlotKey(day, hour));
                                                  setDragOverSlotTime24(snapped);
                                                }
                                              : undefined
                                          }
                                          onDrop={
                                            canDragDrop && canCreateInThisSlot
                                              ? (e) => {
                                                  const snapped = getSnappedQuarterTime24FromEvent(e, hour);
                                                  handleDrop(e, day, snapped);
                                                }
                                              : undefined
                                          }
                                          title={
                                            isInTimeOff
                                              ? allowBookingInTimeOffSlots
                                                ? 'Time off (booking allowed)'
                                                : 'Time off - unavailable'
                                              : isTouchDevice
                                                ? 'Tap to create appointment'
                                                : 'Double-click to create appointment'
                                          }
                                        >
                                          {canDragDrop &&
                                            dragOverSlotKey === getSlotKey(day, hour) &&
                                            dragOverSlotTime24 && (
                                              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/20">
                                                  {dragOverSlotTime24}
                                                </span>
                                              </div>
                                            )}
                                          {/* Time off indicator */}
                                          {isInTimeOff && (
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                              <div className="flex items-center gap-1 text-gray-500 text-sm md:text-xs">
                                                <i className="bi bi-calendar-x"></i>
                                                <span>Time Off</span>
                                              </div>
                                            </div>
                                          )}
                                          {/* Add icon indicator */}
                                          {(!isInTimeOff || allowBookingInTimeOffSlots) && (
                                            <div
                                              className={`absolute inset-0 flex items-center justify-center ${
                                                isTouchDevice ? 'opacity-30' : 'opacity-0 group-hover:opacity-30'
                                              } transition-opacity pointer-events-none`}
                                            >
                                              <Plus className="w-5 h-5 text-blue-600" />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Appointments for this day */}
                                  {(() => {
                                    const dayBlocks = getDayBlocks(day);

                                    // Group overlapping blocks (same as previous logic, but block-based)
                                    const blockGroups: AppointmentBlock[][] = [];
                                    const sortedBlocks = [...dayBlocks].sort((a, b) => a.start.getTime() - b.start.getTime());

                                    sortedBlocks.forEach((block) => {
                                      const blockStart = block.start.getTime();
                                      const blockEnd = block.end.getTime();
                                      let addedToGroup = false;

                                      for (const group of blockGroups) {
                                        const groupEnd = Math.max(...group.map((b) => b.end.getTime()));
                                        const groupStart = Math.min(...group.map((b) => b.start.getTime()));

                                        if (blockStart < groupEnd && blockEnd > groupStart) {
                                          group.push(block);
                                          addedToGroup = true;
                                          break;
                                        }
                                      }

                                      if (!addedToGroup) blockGroups.push([block]);
                                    });

                                    return blockGroups.map((group) =>
                                      group.map((block, idx) => {
                                        const appointment = block.appointment;
                                        const appointmentTime = block.start;
                                        const hour = appointmentTime.getHours();
                                        const minute = appointmentTime.getMinutes();
                                        const topPosition =
                                          ((hour - visibleHourRange.startHour) * 60 + minute) * (HOUR_ROW_HEIGHT_PX / 60);

                                        const calculatedHeight = getEffectiveBlockDurationMinutes(block) * (HOUR_ROW_HEIGHT_PX / 60);
                                        const appointmentHeight = Math.max(40, calculatedHeight);

                                        const spacingPercent = 1;
                                        const containerWidth = 100;
                                        const totalSpacing = spacingPercent * (group.length - 1);
                                        const availableWidth = containerWidth - totalSpacing;
                                        const appointmentWidth = availableWidth / group.length;
                                        const leftPosition = idx * (appointmentWidth + spacingPercent);

                                        const servicesForBlock = block.assignments || [];
                                        const isCanceled = appointment.status === 'CANCELLED';
                                        const blockCategoryBg = isCanceled 
                                          ? '#E5E7EB' // Light gray for canceled appointments
                                          : (servicesForBlock.find(a => a.categoryBackgroundColor)?.categoryBackgroundColor || DEFAULT_CATEGORY_BG) as string;
                                        const blockBorderColor = darkenHex(blockCategoryBg, 0.25);
                                        const pendingDeposit = isPendingDepositOutstanding(appointment);

                                        // Zig-zag pattern for canceled appointments
                                        const zigZagPattern = isCanceled
                                          ? `repeating-linear-gradient(
                                              45deg,
                                              transparent,
                                              transparent 6px,
                                              rgba(0, 0, 0, 0.04) 6px,
                                              rgba(0, 0, 0, 0.04) 12px
                                            )`
                                          : 'none';

                                        return (
                                          <div
                                            key={block.id}
                                            draggable={
                                              canDragDrop &&
                                              appointment.status !== 'CANCELLED' &&
                                              appointment.status !== 'COMPLETED'
                                            }
                                            onDragStart={
                                              canDragDrop &&
                                              appointment.status !== 'CANCELLED' &&
                                              appointment.status !== 'COMPLETED'
                                                ? (e) => handleDragStart(e, appointment)
                                                : undefined
                                            }
                                            onDragEnd={canDragDrop ? handleDragEnd : undefined}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (isDraggingRef.current) return;
                                              if (isAppointmentMoving(appointment)) return;
                                              onAppointmentClick && onAppointmentClick(appointment);
                                            }}
                                            title={pendingDeposit ? 'Pending deposit' : undefined}
                                            className={`
                                              absolute px-2 py-1 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden
                                              text-gray-700
                                              ${pendingDeposit ? 'border-4 border-red-600 border-solid' : 'border-l-4 border-t'}
                                              ${isAppointmentMoving(appointment) ? 'opacity-70 cursor-wait' : ''}
                                            `}
                                            style={{
                                              top: `${topPosition}px`,
                                              left: `${leftPosition}%`,
                                              width: `${appointmentWidth}%`,
                                              height: `${appointmentHeight}px`,
                                              maxHeight: `${appointmentHeight}px`,
                                              zIndex: 10,
                                              boxSizing: 'border-box',
                                              backgroundColor: blockCategoryBg,
                                              backgroundImage: zigZagPattern,
                                              ...(pendingDeposit
                                                ? {}
                                                : {
                                                    borderLeftColor: blockBorderColor,
                                                    borderTopColor: blockBorderColor,
                                                  }),
                                            }}
                                          >
                                            {isAppointmentMoving(appointment) && (
                                              <div className="absolute top-1 right-1 z-10">
                                                <Loader2 className="h-3.5 w-3.5 text-[#0B4778] animate-spin" aria-label="Saving" />
                                              </div>
                                            )}
                          {canResize &&
                            appointment.status !== 'CANCELLED' &&
                            appointment.status !== 'COMPLETED' &&
                            getResizableAssignmentId(block) && (
                              <div
                                className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize"
                                onPointerDown={(e) => startResize(e, block)}
                                onPointerMove={resizing?.blockId === block.id ? onResizeMove : undefined}
                                onPointerUp={resizing?.blockId === block.id ? finishResize : undefined}
                              >
                                <div className="mx-auto mt-0.5 h-1 w-10 rounded-full bg-gray-400/60" />
                              </div>
                            )}
                                            <div className="flex flex-col h-full overflow-hidden justify-start pt-0.5">
                                              <div className="flex items-start gap-1.5 mb-0.5 min-w-0">
                                                {(() => {
                                                  const { title, Icon, badgeClassName, iconClassName } = getBookingInitiatorUi(appointment);
                                                  return (
                                                    <span
                                                      title={title}
                                                      className={`flex-shrink-0 inline-flex items-center justify-center border w-4 h-4 md:w-3 md:h-3 ${badgeClassName}`}
                                                    >
                                                      <Icon className={`h-3 w-3 md:h-2.5 md:w-2.5 ${iconClassName}`} />
                                                    </span>
                                                  );
                                                })()}
                                                <div className="font-semibold text-base md:text-xs leading-snug md:leading-tight min-w-0 truncate text-gray-900">
                                                  {appointment.customer_first_name && appointment.customer_last_name
                                                    ? `${appointment.customer_first_name} ${appointment.customer_last_name}`
                                                    : appointment.customer_first_name || appointment.customer_last_name || 'Customer'}
                                                  {isCanceled && <span className="text-red-500 font-semibold ml-1">(Canceled)</span>}
                                                </div>
                                              </div>

                                              {(() => {
                                                const services = getServiceDisplayLines({ ...appointment, assignments: servicesForBlock });
                                                if (services.length === 0) return null;

                                                const availableHeight = appointmentHeight - (isMobileViewport ? 32 : 22);
                                                const { visible, hasMore } = calculateVisibleServices(availableHeight, services.length);
                                                const visibleServices = services.slice(0, visible);

                                                return (
                                                  <div className="flex flex-col gap-0.5 mt-0.5 overflow-hidden">
                                                    {visibleServices.map((service, idx2) => (
                                                      <div
                                                        key={idx2}
                                                        className="text-sm md:text-xs text-gray-600 truncate leading-snug md:leading-tight flex items-center gap-1"
                                                        title={service.displayText}
                                                      >
                                                        {renderServiceIconBadge(service.icon)}
                                                        <span>{service.displayText}</span>
                                                      </div>
                                                    ))}
                                                    {hasMore && <div className="text-sm md:text-xs text-gray-500 leading-snug md:leading-tight">...</div>}
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        );
                                      })
                                    );
                                  })()}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            // Single day view for regular appointments page
            <>
          {/* Day Summary */}
          {getDayAppointments().length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-blue-900">Day Summary</h3>
                  <p className="text-sm text-blue-700">
                    {getDayAppointments().length} appointment{getDayAppointments().length !== 1 ? 's' : ''} scheduled
                  </p>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-blue-700">
                      {getDayAppointments().filter(a => a.status === 'CONFIRMED').length} Confirmed
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span className="text-blue-700">
                      {getDayAppointments().filter(a => a.status === 'PENDING').length} Pending
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Timeline View */}
          {/* Let the timeline grow naturally; rely on page scroll (avoid nested vertical scrollbars). */}
          <div className="relative">
            <div className="relative" style={{ height: `${visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX}px` }}>
              {(() => {
                if (!isSameLocalDay(selectedDate, now)) return null;
                const topPx = getNowTopPx(visibleHourRange.startHour);
                if (topPx < 0 || topPx > visibleHourRange.slotCount * HOUR_ROW_HEIGHT_PX) return null;
                return <CurrentTimeLine topPx={topPx} leftPx={TIMELINE_TIME_COL_WIDTH_PX} />;
              })()}

              {/* Clickable Time Slots */}
              <div className="absolute inset-0 grid grid-cols-1 gap-0">
                {Array.from({ length: visibleHourRange.slotCount }, (_, i) => {
                  const hour = i + visibleHourRange.startHour;
                  const time24 = toTime24(hour);
                  const isInTimeOff = isTimeSlotInTimeOff(selectedDate, hour);
                  const canCreateInThisSlot = allowBookingInTimeOffSlots || !isInTimeOff;
                  
                  const handleTimeSlotClick = () => {
                    if (onTimeSlotDoubleClick && canCreateInThisSlot) {
                      onTimeSlotDoubleClick(selectedDate, time24);
                    }
                  };
                  
                  return (
                    <div 
                      key={i} 
                      className={`border-b border-gray-100 transition-colors relative group box-border ${
                        isInTimeOff && !allowBookingInTimeOffSlots
                          ? 'bg-gray-100 cursor-not-allowed opacity-60'
                          : isTouchDevice 
                            ? 'cursor-pointer active:bg-blue-50' 
                            : 'hover:bg-blue-50 cursor-pointer'
                      }`}
                      style={{ height: `${HOUR_ROW_HEIGHT_PX}px` }}
                      onClick={isTouchDevice && canCreateInThisSlot ? handleTimeSlotClick : undefined}
                      onDoubleClick={!isTouchDevice && canCreateInThisSlot ? handleTimeSlotClick : undefined}
                      onDragOver={
                        canDragDrop && canCreateInThisSlot
                          ? (e) => {
                              handleDragOver(e, selectedDate);
                              const snapped = getSnappedQuarterTime24FromEvent(e, hour);
                              setDragOverSlotKey(getSlotKey(selectedDate, hour));
                              setDragOverSlotTime24(snapped);
                            }
                          : undefined
                      }
                      onDrop={
                        canDragDrop && canCreateInThisSlot
                          ? (e) => {
                              const snapped = getSnappedQuarterTime24FromEvent(e, hour);
                              handleDrop(e, selectedDate, snapped);
                            }
                          : undefined
                      }
                      title={
                        isInTimeOff 
                          ? (allowBookingInTimeOffSlots ? "Time off (booking allowed)" : "Time off - unavailable")
                          : isTouchDevice 
                            ? "Tap to create appointment" 
                            : "Double-click to create appointment"
                      }
                    >
                      {canDragDrop &&
                        dragOverSlotKey === getSlotKey(selectedDate, hour) &&
                        dragOverSlotTime24 && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/20">
                              {dragOverSlotTime24}
                            </span>
                          </div>
                        )}
                      {/* Time off indicator */}
                      {isInTimeOff && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="flex items-center gap-1 text-gray-500 text-sm md:text-xs">
                            <i className="bi bi-calendar-x"></i>
                            <span>Time Off</span>
                          </div>
                        </div>
                      )}
                      {/* Add icon indicator - more prominent on mobile */}
                      {(!isInTimeOff || allowBookingInTimeOffSlots) && (
                        <div className={`absolute inset-0 flex items-center justify-center ${
                          isTouchDevice 
                            ? 'opacity-30' 
                            : 'opacity-0 group-hover:opacity-30'
                        } transition-opacity pointer-events-none`}>
                          <Plus className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Time Labels */}
              <div className="absolute left-0 top-0 w-16 h-full">
                {Array.from({ length: visibleHourRange.slotCount }, (_, i) => {
                  const hour = i + visibleHourRange.startHour;
                  return (
                    <div key={i} className="flex items-start justify-end pr-2 border-b border-gray-100 box-border" style={{ height: `${HOUR_ROW_HEIGHT_PX}px` }}>
                      <span className="text-sm md:text-xs text-gray-400 font-medium">
                        {toTime24(hour)}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              {/* Appointments Timeline */}
              <div className="ml-16 relative overflow-hidden h-full">
              {getDayAppointments().length > 0 ? (
                (() => {
                  const dayBlocks = getDayBlocks();

                  // Group overlapping blocks
                  const blockGroups: AppointmentBlock[][] = [];
                  const sortedBlocks = [...dayBlocks].sort((a, b) => a.start.getTime() - b.start.getTime());

                  sortedBlocks.forEach((block) => {
                    const blockStart = block.start.getTime();
                    const blockEnd = block.end.getTime();
                    let addedToGroup = false;

                    for (const group of blockGroups) {
                      const groupEnd = Math.max(...group.map((b) => b.end.getTime()));
                      const groupStart = Math.min(...group.map((b) => b.start.getTime()));

                      if (blockStart < groupEnd && blockEnd > groupStart) {
                        group.push(block);
                        addedToGroup = true;
                        break;
                      }
                    }

                    if (!addedToGroup) blockGroups.push([block]);
                  });

                  return blockGroups.map((group) =>
                    group.map((block, appointmentIndex) => {
                      const appointment = block.appointment;
                      const appointmentTime = block.start;
                      const hour = appointmentTime.getHours();
                      const minute = appointmentTime.getMinutes();
                      const topPosition = ((hour - visibleHourRange.startHour) * 60 + minute) * (HOUR_ROW_HEIGHT_PX / 60);

                      const calculatedHeight = getEffectiveBlockDurationMinutes(block) * (HOUR_ROW_HEIGHT_PX / 60);
                      const appointmentHeight = Math.max(40, calculatedHeight);

                      // Calculate width and left position for overlapping blocks
                      const spacingPercent = 0.2; // ~1px
                      const containerWidth = 100;
                      const totalSpacing = spacingPercent * (group.length - 1);
                      const availableWidth = containerWidth - totalSpacing;
                      const appointmentWidth = availableWidth / group.length;
                      const groupWidth = appointmentWidth;
                      const leftPosition = appointmentIndex * (appointmentWidth + spacingPercent);

                      const servicesForBlock = block.assignments || [];
                      const isCanceled = appointment.status === 'CANCELLED';
                      const blockCategoryBg = isCanceled 
                        ? '#E5E7EB' // Light gray for canceled appointments
                        : (servicesForBlock.find(a => a.categoryBackgroundColor)?.categoryBackgroundColor || DEFAULT_CATEGORY_BG) as string;
                      const blockBorderColor = darkenHex(blockCategoryBg, 0.25);
                      const pendingDeposit = isPendingDepositOutstanding(appointment);

                      // Zig-zag pattern for canceled appointments
                      const zigZagPattern = isCanceled
                        ? `repeating-linear-gradient(
                            45deg,
                            transparent,
                            transparent 6px,
                            rgba(0, 0, 0, 0.04) 6px,
                            rgba(0, 0, 0, 0.04) 12px
                          )`
                        : 'none';

                      return (
                        <div
                          key={block.id}
                          draggable={
                          canDragDrop &&
                            appointment.status !== 'CANCELLED' &&
                            appointment.status !== 'COMPLETED'
                          }
                          onDragStart={
                          canDragDrop &&
                            appointment.status !== 'CANCELLED' &&
                            appointment.status !== 'COMPLETED'
                              ? (e) => handleDragStart(e, appointment)
                              : undefined
                          }
                        onDragEnd={canDragDrop ? handleDragEnd : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isDraggingRef.current) return;
                            if (isAppointmentMoving(appointment)) return;
                            onAppointmentClick && onAppointmentClick(appointment);
                          }}
                          title={pendingDeposit ? 'Pending deposit' : undefined}
                          className={`
                            absolute px-2 py-1 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden
                            text-gray-700
                            ${pendingDeposit ? 'border-4 border-red-600 border-solid' : 'border-l-4 border-t'}
                            ${isAppointmentMoving(appointment) ? 'opacity-70 cursor-wait' : ''}
                          `}
                          style={{
                            top: `${topPosition}px`,
                            left: `${leftPosition}%`,
                            width: `${groupWidth}%`,
                            height: `${appointmentHeight}px`,
                            maxHeight: `${appointmentHeight}px`,
                            zIndex: 10,
                            boxSizing: 'border-box',
                            backgroundColor: blockCategoryBg,
                            backgroundImage: zigZagPattern,
                            ...(pendingDeposit
                              ? {}
                              : {
                                  borderLeftColor: blockBorderColor,
                                  borderTopColor: blockBorderColor,
                                }),
                          }}
                        >
                          {isAppointmentMoving(appointment) && (
                            <div className="absolute top-1 right-1 z-10">
                              <Loader2 className="h-3.5 w-3.5 text-[#0B4778] animate-spin" aria-label="Saving" />
                            </div>
                          )}
                          {canResize &&
                            appointment.status !== 'CANCELLED' &&
                            appointment.status !== 'COMPLETED' &&
                            getResizableAssignmentId(block) && (
                              <div
                                className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize"
                                onPointerDown={(e) => startResize(e, block)}
                                onPointerMove={resizing?.blockId === block.id ? onResizeMove : undefined}
                                onPointerUp={resizing?.blockId === block.id ? finishResize : undefined}
                              >
                                <div className="mx-auto mt-0.5 h-1 w-10 rounded-full bg-gray-400/60" />
                              </div>
                            )}
                          <div className="flex flex-col h-full overflow-hidden pt-0.5">
                            <div className="flex items-start justify-between mb-0.5">
                              <div className="flex items-start gap-1.5 flex-1 min-w-0">
                                {(() => {
                                  const { title, Icon, badgeClassName, iconClassName } = getBookingInitiatorUi(appointment);
                                  return (
                                    <span
                                      title={title}
                                      className={`flex-shrink-0 inline-flex items-center justify-center border w-4 h-4 md:w-3 md:h-3 ${badgeClassName}`}
                                    >
                                      <Icon className={`h-3 w-3 md:h-2.5 md:w-2.5 ${iconClassName}`} />
                                    </span>
                                  );
                                })()}
                                <div className="font-semibold text-base md:text-xs leading-snug md:leading-tight min-w-0 truncate text-gray-900">
                                  {appointment.customer_first_name && appointment.customer_last_name
                                    ? `${appointment.customer_first_name} ${appointment.customer_last_name}`
                                    : appointment.customer_first_name || appointment.customer_last_name || 'Customer'}
                                  {isCanceled && <span className="text-red-500 font-semibold ml-1">(Canceled)</span>}
                                </div>
                              </div>
                              <div className="flex-shrink-0 ml-1 flex items-center">
                                <div className={`w-2 h-2 rounded-full ${getStatusColor(appointment.status)}`}></div>
                              </div>
                            </div>

                            {(() => {
                              const services = getServiceDisplayLines({ ...appointment, assignments: servicesForBlock });
                              if (services.length === 0) return null;

                              const reservedHeight = isMobileViewport ? 8 + 22 + 18 : 8 + 14 + 14;
                              const availableHeight = appointmentHeight - reservedHeight;
                              const { visible, hasMore } = calculateVisibleServices(availableHeight, services.length);
                              const visibleServices = services.slice(0, visible);

                              return (
                                <div className="flex flex-col gap-0.5 mb-0.5 overflow-hidden">
                                  {visibleServices.map((service, idx2) => {
                                    return (
                                      <div key={idx2} className="text-sm md:text-xs text-gray-600 truncate leading-snug md:leading-tight flex items-center gap-1" title={service.displayText}>
                                        {renderServiceIconBadge(service.icon)}
                                        <span>{service.displayText}</span>
                                      </div>
                                    );
                                  })}
                                  {hasMore && (
                                    <div className="text-sm md:text-xs text-gray-500 leading-snug md:leading-tight">...</div>
                                  )}
                                </div>
                              );
                            })()}

                            <div className="flex items-center space-x-1 text-sm md:text-xs text-gray-600 leading-snug md:leading-tight">
                              <span>{formatTime(block.start.toISOString())}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  );
                })()
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl mb-4">📅</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No appointments scheduled</h3>
                    <p className="text-gray-600 mb-4">
                      {selectedDate.toDateString() === new Date().toDateString() 
                        ? "No appointments for today" 
                        : `No appointments for ${selectedDate.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })}`
                      }
                    </p>
                    {onAddAppointment && (
                      <button
                        onClick={() => onAddAppointment(selectedDate, '09:00')}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Appointment</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar; 